import { useRef, useEffect } from 'react';

import { useLocation } from 'react-router-dom';

import {
  getStoredNavigationContext,
  storeNavigationContext,
  createNavigationContext,
  clearSourceNavigationContext,
  clearNavigationContext
} from '../utils/secureStorage';

/**
 * Custom hook for managing navigation context
 * Handles storing and retrieving navigation context from session storage
 * @returns {object} Navigation context object
 */
export const useNavigationContext = () => {
  const location = useLocation();
  const entryContextRef = useRef(null);

  useEffect(() => {
    if (!entryContextRef.current) {
      // Try to get existing context from session storage
      const storedContext = getStoredNavigationContext();

      // Create navigation context (this handles source context internally)
      const context = createNavigationContext(location);

      // Use new context if it has a referrer, otherwise fall back to stored context
      const finalContext = context.referrer ? context : storedContext || context;

      entryContextRef.current = finalContext;

      // Store context if it's new or if we're updating with better data
      if (!storedContext || finalContext.referrer !== storedContext.referrer) {
        storeNavigationContext(finalContext);

        // Clean up source context after successfully storing navigation context
        if (finalContext.sourceContext) {
          clearSourceNavigationContext();
        }
      }
    }
  }, [location.pathname, location]);

  /**
   * Get the current navigation context
   * @returns {object|null} Current navigation context
   */
  const getContext = () => {
    return entryContextRef.current || getStoredNavigationContext();
  };

  /**
   * Update the navigation context
   * @param {object} updates - Updates to apply to the context
   */
  const updateContext = (updates) => {
    const currentContext = getContext();
    const updatedContext = { ...currentContext, ...updates };

    entryContextRef.current = updatedContext;
    storeNavigationContext(updatedContext);
  };

  /**
   * Clear the navigation context completely
   * Call this when closing chat to ensure fresh context on next visit
   */
  const clearContext = () => {
    entryContextRef.current = null;
    clearNavigationContext();
    clearSourceNavigationContext();
  };

  return {
    context: entryContextRef.current,
    getContext,
    updateContext,
    clearContext
  };
};
